import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BALL_RADIUS, CANVAS_HEIGHT, FLOOR_HEIGHT, PLAYABLE_AREA, WALL_THICKNESS } from '../constants';
import { convertHeightToDisplayUnits } from '../utils';

/**
 * New Height Notification UI matching the design from new-main.html
 * Shows "NEW HEIGHT!" notification when player reaches a new maximum height
 * Also creates a visual marker in the scene at that height
 */
export default class NewHeightNotification {
    private container: HTMLDivElement | null = null;
    private isVisible: boolean = false;
    private lastHeight: number = 0;
    private hideTimeout: number | null = null;
    private gameWorld: Container | null = null;
    private heightMarker: Container | null = null;
    private markerGraphics: Graphics | null = null;
    private markerLabel: Text | null = null;
    private markerTargetY: number | null = null;
    private markerAnimationFrame: number | null = null;

    constructor(gameWorld?: Container) {
        this.gameWorld = gameWorld || null;
        this.createNotification();
    }

    private createNotification(): void {
        this.container = document.createElement('div');
        this.container.className = 'golfup-new-height-notification';
        this.container.style.cssText = `
            position: fixed;
            top: 8rem;
            left: 50%;
            transform: translateX(-50%);
            z-index: 50;
            display: none;
            flex-direction: column;
            align-items: center;
            pointer-events: none;
        `;

        const innerDiv = document.createElement('div');
        innerDiv.style.cssText = `
            background: #FCD34D;
            border: 4px solid black;
            padding: 8px 24px;
            transform: rotate(2deg);
            box-shadow: 6px 6px 0 rgba(0, 0, 0, 0.2);
            z-index: 20;
        `;

        const heading = document.createElement('h2');
        heading.textContent = 'NEW HEIGHT!';
        heading.style.cssText = `
            font-size: 1.25rem;
            font-weight: 900;
            color: white;
            letter-spacing: 0.05em;
            font-family: 'Fredoka', sans-serif;
            -webkit-text-stroke: 1.5px black;
            text-stroke: 1.5px black;
            margin: 0;
        `;

        innerDiv.appendChild(heading);
        this.container.appendChild(innerDiv);
        document.body.appendChild(this.container);
    }

    /**
     * Check if a new height was reached and show notification
     */
    public checkNewHeight(currentHeight: number): void {
        // Only show if height increased by at least 1 unit (to avoid spam)
        if (currentHeight > this.lastHeight + 0.5) {
            const newRecordHeight = currentHeight;
            this.lastHeight = currentHeight;
            this.show();
            this.createHeightMarker(newRecordHeight);
        }
    }

    /**
     * Create a visual marker in the scene at the new height
     */
    private createHeightMarker(height: number): void {
        if (!this.gameWorld) return;

        // Calculate Y position
        // height = CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS - ball.position.y
        // => ball.position.y = CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS - height
        const yPosition = CANVAS_HEIGHT - FLOOR_HEIGHT - BALL_RADIUS - height;

        // Lazily create marker container and graphics
        if (!this.heightMarker) {
            this.heightMarker = new Container();
            this.markerGraphics = new Graphics();
            this.markerGraphics.roundPixels = false;
            this.heightMarker.addChild(this.markerGraphics);

            this.markerLabel = new Text({
                text: '',
                style: new TextStyle({
                    fontFamily: 'Fredoka',
                    fontSize: 10,
                    fill: 0x000000,
                    fontWeight: 'bold',
                })
            });
            this.markerLabel.anchor.set(0.5, 0.5);
            this.heightMarker.addChild(this.markerLabel);

            this.heightMarker.zIndex = 100;
            this.gameWorld.addChild(this.heightMarker);
        }

        if (!this.markerGraphics || !this.markerLabel) return;

        // Draw a horizontal line with a small flag (relative to container origin)
        const lineWidth = PLAYABLE_AREA;
        const lineX = WALL_THICKNESS;
        const flagSize = 24;
        const flagX = lineX + lineWidth - flagSize - 4;

        this.markerGraphics.clear();
        this.markerGraphics.setStrokeStyle({ width: 2, color: 0xFFD93D, alpha: 0.7 });
        this.markerGraphics.moveTo(lineX, 0);
        this.markerGraphics.lineTo(lineX + lineWidth, 0);
        this.markerGraphics.stroke();

        // Flag background
        this.markerGraphics.rect(flagX, -flagSize / 2, flagSize, flagSize);
        this.markerGraphics.fill({ color: 0xFFD93D, alpha: 0.9 });
        this.markerGraphics.stroke({ width: 2, color: 0x000000 });

        // Update text label
        this.markerLabel.text = `${Math.round(convertHeightToDisplayUnits(height))}m`;
        this.markerLabel.x = flagX + flagSize / 2;
        this.markerLabel.y = 0;

        const previousTarget = this.markerTargetY;
        this.markerTargetY = yPosition;

        if (previousTarget === null) {
            // First placement, snap immediately
            this.heightMarker.y = yPosition;
        } else {
            this.animateMarkerToTarget();
        }
    }

    private animateMarkerToTarget(): void {
        if (!this.heightMarker || this.markerTargetY === null) return;

        if (this.markerAnimationFrame !== null) {
            cancelAnimationFrame(this.markerAnimationFrame);
        }

        const animate = () => {
            if (!this.heightMarker || this.markerTargetY === null) {
                this.markerAnimationFrame = null;
                return;
            }

            const currentY = this.heightMarker.y;
            const diff = this.markerTargetY - currentY;

            if (Math.abs(diff) < 0.5) {
                this.heightMarker.y = this.markerTargetY;
                this.markerAnimationFrame = null;
                return;
            }

            this.heightMarker.y = currentY + diff * 0.12;
            this.markerAnimationFrame = requestAnimationFrame(animate);
        };

        this.markerAnimationFrame = requestAnimationFrame(animate);
    }

    /**
     * Show the notification with bounce animation
     */
    private show(): void {
        if (!this.container) return;

        // Clear any existing timeout
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }

        // Show the notification
        this.container.style.display = 'flex';
        this.isVisible = true;

        // Add bounce animation by triggering a reflow
        void this.container.offsetWidth;
        this.container.style.animation = 'none';
        void this.container.offsetWidth;
        this.container.style.animation = 'golfup-bounce 1s ease-in-out';

        // Hide after 2 seconds
        this.hideTimeout = window.setTimeout(() => {
            this.hide();
        }, 2000);
    }

    /**
     * Hide the notification
     */
    private hide(): void {
        if (!this.container || !this.isVisible) return;
        
        this.container.style.display = 'none';
        this.isVisible = false;
        
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }

    /**
     * Reset the last height (useful when starting a new round)
     */
    public reset(): void {
        this.lastHeight = 0;
        this.hide();
        // Remove marker
        if (this.heightMarker && this.heightMarker.parent) {
            this.gameWorld?.removeChild(this.heightMarker);
            this.heightMarker.destroy({ children: true });
            this.heightMarker = null;
            this.markerGraphics = null;
            this.markerLabel = null;
            this.markerTargetY = null;
        }
        if (this.markerAnimationFrame) {
            cancelAnimationFrame(this.markerAnimationFrame);
            this.markerAnimationFrame = null;
        }
    }

    /**
     * Clean up
     */
    public destroy(): void {
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }
        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
        if (this.heightMarker && this.heightMarker.parent) {
            this.gameWorld?.removeChild(this.heightMarker);
            this.heightMarker.destroy({ children: true });
        }
        if (this.markerAnimationFrame) {
            cancelAnimationFrame(this.markerAnimationFrame);
        }
        this.container = null;
        this.heightMarker = null;
        this.markerGraphics = null;
        this.markerLabel = null;
        this.markerTargetY = null;
        this.markerAnimationFrame = null;
    }
}

